import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, MenuItem, FormControl, InputLabel, Select, Typography, Grid, Box, Checkbox, List, ListItem, ListItemText, LinearProgress
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { v4 as uuidv4 } from 'uuid';
import { getFirestore, doc, getDoc, getDocs, updateDoc, addDoc, collection } from 'firebase/firestore';
import { app } from './auth/firebaseConfig';
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Comments from './Comments';
import { fetchTenantUsers, fetchLastSerialNumber, updateLastSerialNumber, fetchAssignedGroupsByEmail } from '../DatabaseApis/DBQuery';
import { Divider } from "@mui/material";
import { getCookie } from '../utils/cookieUtils';
import { saveNewCard } from "../utils/firestoreCards";
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import {storage} from './auth/firebaseConfig';

// Helper to format timestamps for logs
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown Date';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Unknown Date';
  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  const [datePart, timePart] = formattedDate.split(', ');
  return `${datePart.replace(/ /g, "/")}, ${timePart || ''}`;
};

const priorityOptions = ["Low", "Medium", "High", "Critical"];
const ragStatusOptions = ["Red", "Amber", "Green"];
const currentStateOptions = ["New", "Analysis", "Review", "In Progress", "Resolved","Reopen", "Verification", "Closed", "Blocked","Rejected"];
const MAX_FILE_COUNT = 10;
const MAX_FILE_SIZE_MB = 5;

const AddIssueCardDialog = ({ open, onClose, onSave, prjName, boardId, projectId, tenantId, userId, userName, initialData }) => {
  // Columns for status selection
  const [columns, setColumns] = useState([]);
  
  // Drag and drop/file upload handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (uploadedFiles.length + files.length > MAX_FILE_COUNT) {
      alert(`Cannot upload ${files.length} files. You can only upload up to ${MAX_FILE_COUNT} files total. Currently ${uploadedFiles.length} files uploaded.`);
      return;
    }
    files.forEach(file => uploadFile(file));
  };

  const uploadFile = async (file) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    if (uploadedFiles.length >= MAX_FILE_COUNT) {
      alert(`Maximum number of files (${MAX_FILE_COUNT}) reached. Please delete some files before uploading more.`);
      return;
    }

    try {
      const fileId = uuidv4();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const filePath = `card-images/${fileId}_${sanitizedFileName}`;
      
      const fileRef = ref(storage, filePath);
      const uploadTask = uploadBytesResumable(fileRef, file);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: progress
          }));
        },
        (error) => {
          console.error('Upload error:', error);
          alert('Error uploading file. Please try again.');
        },
        async () => {
          if (uploadedFiles.length >= MAX_FILE_COUNT) {
            alert(`Maximum number of files (${MAX_FILE_COUNT}) reached. The upload will be discarded.`);
            await deleteObject(fileRef);
            return;
          }

          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          setUploadedFiles(prev => {
            if (prev.length >= MAX_FILE_COUNT) {
              alert(`Maximum number of files (${MAX_FILE_COUNT}) reached. The upload will be discarded.`);
              deleteObject(fileRef);
              return prev;
            }
            return [...prev, {
              id: fileId,
              name: file.name,
              url: downloadURL,
              uploadedAt: new Date().toISOString()
            }];
          });
          
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
        }
      );
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (uploadedFiles.length + files.length > MAX_FILE_COUNT) {
      alert(`Cannot upload ${files.length} files. You can only upload up to ${MAX_FILE_COUNT} files total. Currently ${uploadedFiles.length} files uploaded.`);
      return;
    }
    files.forEach(file => uploadFile(file));
    event.target.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    const confirmed = window.confirm('Are you sure you want to delete this file?');
    if (!confirmed) return;

    try {
      const fileToDelete = uploadedFiles.find(f => f.id === fileId);
      if (!fileToDelete) return;

      const url = new URL(fileToDelete.url);
      const filePath = decodeURIComponent(url.pathname.split('/o/')[1]);
      
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
      
      setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    } catch (error) {
      console.error("Error deleting file:", error);
      alert('Error deleting file. Please try again.');
    }
  };

  const handleBoardChange = (newBoardId) => {
    setSelectedBoard(newBoardId);
    const fetchColumns = async () => {
      try {
        const db = getFirestore(app);
        const boardRef = doc(db, "boards", newBoardId);
        const boardSnap = await getDoc(boardRef);
        let cols = [];
        if (boardSnap.exists()) {
          const boardData = boardSnap.data();
          cols = Array.isArray(boardData.columns) ? boardData.columns : [];
          if (cols.length > 0 && typeof cols[0] === "string") {
            cols = cols.map((col, idx) => ({ id: `column-${idx + 1}`, name: col }));
          }
        }
        if (!cols || cols.length === 0) {
          cols = currentStateOptions.map((name, idx) => ({ id: `column-${idx + 1}`, name }));
        }
        setColumns(cols);
        // Only set default currentState if creating a new card (no initialData), don't overwrite when editing
        if (!initialData && !currentState && cols.length > 0) {
          setCurrentState(cols[0].name);
        }
      } catch (err) {
        console.error("Error fetching columns for board:", err);
      }
    };
    fetchColumns();
  };

  // State variables
  const [issueSummary, setIssueSummary] = useState(initialData?.summary || initialData?.templateData?.issueSummary || '');
  const [detailedDescription, setDetailedDescription] = useState(initialData?.description || initialData?.templateData?.detailedDescription || '');
  const [reportedBy, setReportedBy] = useState(initialData?.templateData?.reportedBy || '');
  const [reportedDate, setReportedDate] = useState(initialData?.templateData?.reportedDate || new Date().toISOString().split('T')[0]);
  const [assignedTo, setAssignedTo] = useState(initialData?.templateData?.assignedTo || initialData?.assigneeName || '');
  const [priority, setPriority] = useState(initialData?.templateData?.priority || '');
  const [currentState, setCurrentState] = useState(initialData?.cardStatus || '');
  const [rootCause, setRootCause] = useState(initialData?.templateData?.rootCause || '');
  const [resolutionSummary, setResolutionSummary] = useState(initialData?.templateData?.resolutionSummary || '');
  const [dateClosed, setDateClosed] = useState(initialData?.templateData?.dateClosed || '');
  const [expectedClosureDate, setExpectedClosureDate] = useState(initialData?.templateData?.expectedClosureDate || '');
  const [actualClosureDate, setActualClosureDate] = useState(initialData?.templateData?.actualClosureDate || '');
  const [dateOfReopening, setDateOfReopening] = useState(initialData?.templateData?.dateOfReopening || '');
  const [ragStatus, setRagStatus] = useState(initialData?.templateData?.ragStatus || 'Amber');
  const [ragStatusHistory, setRagStatusHistory] = useState(initialData?.templateData?.ragStatusHistory || []);
  const [originalRagStatus, setOriginalRagStatus] = useState(initialData?.templateData?.ragStatus || 'Amber');
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFilesToDelete, setSelectedFilesToDelete] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [userList, setUserList] = useState([]);
  const [lastSerialNumber, setLastSerialNumber] = useState(0);
  const [taskNameError, setTaskNameError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingCard, setPendingCard] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [editingCardId, setEditingCardId] = useState(null);
  const [boardList, setBoardList] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(boardId || "");
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const userData = getCookie('userData');
  const auth = getAuth();
  const email = auth.currentUser?.email;

  // Compute safe Select values to avoid MUI out-of-range warnings while options load
  const boardIds = Array.isArray(boardList) ? boardList.map(b => b.id) : [];
  const safeSelectedBoard = boardIds.includes(selectedBoard) ? selectedBoard : '';
  const userNames = Array.isArray(userList) ? userList.map(u => u.userName) : [];
  const safeReportedBy = userNames.includes(reportedBy) ? reportedBy : '';
  const safeAssignedTo = userNames.includes(assignedTo) ? assignedTo : '';
  const prioritySet = new Set(priorityOptions);
  const safePriority = prioritySet.has(priority) ? priority : '';
  const columnNames = Array.isArray(columns) ? columns.map(c => c.name) : [];
  const safeCurrentState = columnNames.includes(currentState) ? currentState : '';

  // Set current user info when component mounts or user data changes
  useEffect(() => {
    if (email && userData) {
      setCurrentUserInfo({
        uid: userData.userId || auth.currentUser?.uid,
        email: email,
        displayName: userData.userName || email.split('@')[0]
      });
    }
  }, [email, userData?.userId, userData?.userName]);

  // Initialize form fields when dialog opens or initialData changes
  useEffect(() => {
    if (open && initialData) {
      setEditingCardId(initialData.id);
      
      setIssueSummary(initialData.summary || initialData.templateData?.issueSummary || '');
      setDetailedDescription(initialData.description || initialData.templateData?.detailedDescription || '');
      setReportedBy(initialData.templateData?.reportedBy || '');
      setReportedDate(initialData.templateData?.reportedDate || new Date().toISOString().split('T')[0]);
      setAssignedTo(initialData.templateData?.assignedTo || initialData.assigneeName || '');
      setPriority(initialData.templateData?.priority || '');
      setCurrentState(initialData.cardStatus || '');
      setRootCause(initialData.templateData?.rootCause || '');
      setResolutionSummary(initialData.templateData?.resolutionSummary || '');
      setDateClosed(initialData.templateData?.dateClosed || '');
      setExpectedClosureDate(initialData.templateData?.expectedClosureDate || '');
      setActualClosureDate(initialData.templateData?.actualClosureDate || '');
      setDateOfReopening(initialData.templateData?.dateOfReopening || '');
      const initialRagStatus = initialData.templateData?.ragStatus || 'Amber';
      setRagStatus(initialRagStatus);
      setOriginalRagStatus(initialRagStatus);
      setRagStatusHistory(initialData.templateData?.ragStatusHistory || []);
      setComments(initialData.templateData?.comments || []);
      setUploadedFiles(initialData.templateData?.attachments || []);
      
      if (initialData.cardId) {
        const serialNumber = Number(initialData.cardId.split('-').pop());
        setLastSerialNumber(serialNumber);
      }
    } else if (open && !initialData) {
      setEditingCardId(null);
      setIssueSummary('');
      setDetailedDescription('');
      setReportedBy('');
      setReportedDate(new Date().toISOString().split('T')[0]);
      setAssignedTo('');
      setPriority('');
      setCurrentState('');
      setRootCause('');
      setResolutionSummary('');
      setDateClosed('');
      setExpectedClosureDate('');
      setActualClosureDate('');
      setDateOfReopening('');
      setRagStatus('Amber');
      setOriginalRagStatus('Amber');
      setRagStatusHistory([]);
      setComments([]);
      setUploadedFiles([]);
      setLastSerialNumber(0);
    }
  }, [open, initialData]);

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const db = getFirestore(app);
        const boardRef = doc(db, "boards", boardId);
        const boardSnap = await getDoc(boardRef);
        let cols = [];
        if (boardSnap.exists()) {
          const boardData = boardSnap.data();
          cols = Array.isArray(boardData.columns) ? boardData.columns : [];
          if (cols.length > 0 && typeof cols[0] === 'string') {
            cols = cols.map((col, idx) => ({ id: `column-${idx+1}`, name: col }));
          }
        }
        if (!cols || cols.length === 0) {
          cols = currentStateOptions.map((name, idx) => ({ id: `column-${idx+1}`, name }));
        }
        setColumns(cols);
        // Only set default currentState if no initialData (creating new card, not editing)
        if (!initialData && !currentState && cols.length > 0) {
          setCurrentState(cols[0].name);
        }
      } catch (error) {
        setColumns(currentStateOptions.map((name, idx) => ({ id: `column-${idx+1}`, name })));
      }
    };
    if (open && boardId) fetchColumns();
    
    const fetchUsers = async () => {
      try {
        if (!tenantId) return;
        const users = await fetchTenantUsers(tenantId);
        setUserList(users);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    if (open && tenantId) fetchUsers();
  }, [open, boardId, initialData, tenantId]);

  useEffect(() => {
    if (open) {
      const getLastSerialNumber = async () => {
        try {
          const serialNumber = await fetchLastSerialNumber(projectId);
          setLastSerialNumber(serialNumber);
        } catch (error) {
          console.error("Failed to fetch serial number");
        }
      };
      getLastSerialNumber();
    }
  }, [open, projectId]);

  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const db = getFirestore(app);
        const boardsRef = collection(db, "boards");
        const querySnapshot = await getDocs(boardsRef);
        const boards = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.projectId === projectId) {
            boards.push({ id: docSnap.id, name: data.boardName || "Untitled Board" });
          }
        });
        setBoardList(boards);
        if (boardId) {
          setSelectedBoard(boardId);
        } else if (boards.length > 0) {
          setSelectedBoard(boards[0].id);
        }
      } catch (err) {
        console.error("Error fetching boards:", err);
      }
    };

    if (open && projectId) fetchBoards();
  }, [open, projectId, boardId]);

  // Handle RAG status change (only update state, history will be saved on save)
  const handleRagStatusChange = (newRagStatus) => {
    if (newRagStatus !== ragStatus) {
      setRagStatus(newRagStatus);
      // For new cards, update originalRagStatus to match so no history is created on first save
      if (!editingCardId) {
        setOriginalRagStatus(newRagStatus);
      }
    }
  };

  // Helper to flush all fields
  const flushFields = () => {
    setIssueSummary("");
    setDetailedDescription("");
    setReportedBy("");
    setReportedDate(new Date().toISOString().split('T')[0]);
    setAssignedTo("");
    setPriority("");
    setCurrentState("");
    setRootCause("");
    setResolutionSummary("");
    setDateClosed("");
    setExpectedClosureDate("");
    setActualClosureDate("");
    setDateOfReopening("");
    setRagStatus("Amber");
    setOriginalRagStatus("Amber");
    setRagStatusHistory([]);
    setComments([]);
    setUploadedFiles([]);
    setTaskNameError(false);
  };

  const handleSave = async () => {
    if (!issueSummary.trim()) {
      setTaskNameError(true);
      return;
    } else {
      setTaskNameError(false);
    }

    const currentDate = new Date();
    const cardCreationDate = (initialData && !initialData.isDuplicate) ? initialData.cardCreationDate : currentDate.toISOString();
    const cardUpdatedOn = currentDate.toISOString();

    const currentUserName = userData?.userName || currentUserInfo?.displayName || 'Unknown User';

    // Find assignee email from userList
    let assigneeEmail = '';
    if (Array.isArray(userList) && assignedTo) {
      const assigneeObj = userList.find(u => u.userName === assignedTo);
      if (assigneeObj && assigneeObj.email) assigneeEmail = assigneeObj.email;
    }

    // Get current user info for updated by
    const updatedByUserId = currentUserInfo?.uid || userId || '';
    const updatedByUserEmail = currentUserInfo?.email || email || '';

    // For created by, preserve original creator if editing
    let createdByUserId = updatedByUserId;
    let createdByUserEmail = updatedByUserEmail;
    if (editingCardId && initialData) {
      createdByUserId = initialData.cardCreatedByUserId || updatedByUserId;
      createdByUserEmail = initialData.cardCreatedByUserEmail || updatedByUserEmail;
    }

    console.log("User info:", { createdByUserId, createdByUserEmail, currentUserName });

    // Check if RAG status changed and update history only when saving
    // Only create history for existing cards (edits), not for new cards
    let finalRagStatusHistory = ragStatusHistory;
    if (editingCardId && ragStatus !== originalRagStatus) {
      const ragChange = {
        previousStatus: originalRagStatus,
        currentStatus: ragStatus,
        dateTime: cardUpdatedOn,
        user: currentUserInfo?.displayName || currentUserInfo?.email || 'Unknown User'
      };
      finalRagStatusHistory = [...ragStatusHistory, ragChange];
    } else if (!editingCardId) {
      // For new cards, initialize with empty history regardless of initial RAG status
      finalRagStatusHistory = [];
    }

    const cardPayload = {
      boardId: selectedBoard,
      projectId,
      tenantId,
      summary: issueSummary,
      description: detailedDescription,
      cardStatus: currentState,
      priority: priority,
      dueDate: expectedClosureDate,
      assigneeName: assignedTo,
      assigneeEmail: assigneeEmail,
      cardType: "Issue",
      cardCreatedByUserId: createdByUserId,
      cardCreatedByUserEmail: createdByUserEmail,
      cardCreationDate: cardCreationDate,
      cardCreationDateDisplay: cardCreationDate,
      cardUpdatedByUserId: updatedByUserId,
      cardUpdatedByUserEmail: updatedByUserEmail,
      cardUpdatedOn: cardUpdatedOn,
      templateData: {
        issueSummary,
        detailedDescription,
        reportedBy,
        reportedDate,
        assignedTo,
        priority,
        rootCause,
        resolutionSummary,
        dateClosed,
        expectedClosureDate,
        actualClosureDate,
        dateOfReopening,
        ragStatus,
        ragStatusHistory: finalRagStatusHistory,
        comments,
        attachments: uploadedFiles,
      },
    };

    // Only add cardId for existing cards (updates), not for duplicates
    if (initialData && !initialData.isDuplicate) {
      cardPayload.cardId = initialData.cardId;
    }

    if (email) {
      setCurrentUserInfo({
        uid: userId,
        email: email
      });
    }

    try {
      if (editingCardId) {
        // Update existing card
        const db = getFirestore(app);
        const cardRef = doc(db, "cards", editingCardId);
        await updateDoc(cardRef, {
          ...cardPayload,
          cardUpdatedByUserId: userId,
          cardUpdatedOn: cardUpdatedOn,
          updatedAt: currentDate.toISOString(),
        });
        console.log("Issue card updated successfully");
      } else {
        // Add new card with Firestore transaction-safe counter
        const newCardId = await saveNewCard(projectId, prjName, cardPayload);
        cardPayload.cardId = newCardId;
        console.log("New issue card created successfully with ID:", newCardId);
      }
    } catch (err) {
      console.error("Error saving Issue card:", err);
      console.error("Error details:", {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      alert(`Failed to save issue card. Error: ${err.message}. Please try again.`);
      return;
    }

    // Flush fields after save
    flushFields();

    // Close dialog - parent component will handle refreshing the data
    onClose();
  };

  // Flush fields on cancel
  const handleCancel = () => {
    flushFields();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
    >
      <Box sx={{ background: 'linear-gradient(45deg, #1e88e5 30%, #66bb6a 90%)', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>
        <DialogTitle sx={{ color: 'white' }}>
          {(initialData && !initialData.isDuplicate) ? 'Edit Card' : 'Add Card'}
        </DialogTitle>
      </Box>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Card ID (non-editable) */}
          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Issue ID"
              fullWidth
              variant="outlined"
              value={initialData?.cardId || (pendingCard && pendingCard.cardId ? pendingCard.cardId : '')}
              InputProps={{ readOnly: true, style: { backgroundColor: '#f5f5f5' } }}
              helperText="Issue ID is auto-generated and cannot be edited"
            />
          </Grid>

          {/* Issue Summary */}
          <Grid item xs={12}>
            <TextField 
              autoFocus 
              margin="dense" 
              label="Issue Summary" 
              fullWidth 
              variant="outlined" 
              value={issueSummary} 
              onChange={(e) => setIssueSummary(e.target.value.slice(0, 150))} 
              error={taskNameError} 
              helperText={taskNameError ? 'Issue Summary is required' : `${issueSummary.length}/150`} 
              inputProps={{ maxLength: 150 }} 
            />
          </Grid>

          {/* Detailed Description */}
          <Grid item xs={12}>
            <TextField 
              margin="dense" 
              label="Detailed Description" 
              fullWidth 
              variant="outlined" 
              multiline 
              rows={4} 
              value={detailedDescription} 
              onChange={(e) => setDetailedDescription(e.target.value.slice(0, 500))} 
              helperText={`${detailedDescription.length}/500`} 
              inputProps={{ maxLength: 500 }} 
            />
          </Grid>

          {/* Board Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="board-select-label">Select Board</InputLabel>
              <Select
                labelId="board-select-label"
                value={safeSelectedBoard}
                onChange={(e) => handleBoardChange(e.target.value)}
                label="Select Board"
                displayEmpty
              >
                {boardList.map((board) => (
                  <MenuItem key={board.id} value={board.id}>
                    {board.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Reported By */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="reported-by-label">Reported By</InputLabel>
              <Select 
                labelId="reported-by-label" 
                value={safeReportedBy} 
                onChange={(e) => setReportedBy(e.target.value)} 
                label="Reported By"
                displayEmpty
              >
                {[...userList].sort((a, b) => a.userName.localeCompare(b.userName)).map((user) => (
                  <MenuItem key={user.id} value={user.userName}>{user.userName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Reported Date */}
          <Grid item xs={12} sm={6}>
            <TextField 
              margin="dense" 
              label="Reported Date" 
              type="date" 
              fullWidth 
              variant="outlined" 
              InputLabelProps={{ shrink: true }} 
              value={reportedDate} 
              onChange={(e) => setReportedDate(e.target.value)} 
            />
          </Grid>

          {/* Assigned To */}
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="assigned-to-label">Assigned To</InputLabel>
              <Select 
                labelId="assigned-to-label" 
                value={safeAssignedTo} 
                onChange={(e) => setAssignedTo(e.target.value)} 
                label="Assigned To"
                displayEmpty
              >
                {[...userList].sort((a, b) => a.userName.localeCompare(b.userName)).map((user) => (
                  <MenuItem key={user.id} value={user.userName}>{user.userName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Priority */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="priority-label">Priority</InputLabel>
              <Select 
                labelId="priority-label" 
                value={safePriority} 
                onChange={(e) => setPriority(e.target.value)} 
                label="Priority"
                displayEmpty
              >
                {priorityOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status/Column */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="card-status-label">Status/Column</InputLabel>
              <Select 
                labelId="card-status-label" 
                value={safeCurrentState} 
                onChange={(e) => setCurrentState(e.target.value)} 
                label="Status/Column"
                displayEmpty
              >
                {columns.map((col) => (
                  <MenuItem key={col.id || col.name} value={col.name}>{col.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* RAG Status Section */}
          <Grid item xs={12}>
            <Divider sx={{
              my: 3,
              borderBottomWidth: 2,
              borderColor: "#4e54c8",
              borderRadius: 2,
              borderStyle: "dashed",
            }} />
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="rag-status-label">RAG Status</InputLabel>
              <Select 
                labelId="rag-status-label" 
                value={ragStatus} 
                onChange={(e) => handleRagStatusChange(e.target.value)} 
                label="RAG Status"
              >
                {ragStatusOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Expected Closure Date */}
          <Grid item xs={12}>
            <TextField 
              margin="dense" 
              label="Expected Closure Date" 
              type="date" 
              fullWidth 
              variant="outlined" 
              InputLabelProps={{ shrink: true }} 
              value={expectedClosureDate} 
              onChange={(e) => setExpectedClosureDate(e.target.value)} 
              inputProps={{ min: new Date().toISOString().split("T")[0] }} 
            />
          </Grid>

          {/* RAG change reason removed as per requirements; only RAG history is shown */}

          {/* Actual Closure Date */}
          <Grid item xs={12}>
            <TextField 
              margin="dense" 
              label="Actual Closure Date" 
              type="date" 
              fullWidth 
              variant="outlined" 
              InputLabelProps={{ shrink: true }} 
              value={actualClosureDate} 
              onChange={(e) => setActualClosureDate(e.target.value)} 
            />
          </Grid>

          {/* Date of Reopening */}
          <Grid item xs={12}>
            <TextField 
              margin="dense" 
              label="Date of Reopening" 
              type="date" 
              fullWidth 
              variant="outlined" 
              InputLabelProps={{ shrink: true }} 
              value={dateOfReopening} 
              onChange={(e) => setDateOfReopening(e.target.value)} 
            />
          </Grid>


          {/* Root Cause */}
          <Grid item xs={12}>
            <TextField 
              margin="dense" 
              label="Root Cause" 
              fullWidth 
              variant="outlined" 
              multiline 
              rows={3} 
              value={rootCause} 
              onChange={(e) => setRootCause(e.target.value.slice(0, 500))} 
              helperText={`${rootCause.length}/500`} 
              inputProps={{ maxLength: 500 }} 
            />
          </Grid>

          {/* Resolution Summary */}
          <Grid item xs={12}>
            <TextField 
              margin="dense" 
              label="Resolution Summary" 
              fullWidth 
              variant="outlined" 
              multiline 
              rows={3} 
              value={resolutionSummary} 
              onChange={(e) => setResolutionSummary(e.target.value.slice(0, 500))} 
              helperText={`${resolutionSummary.length}/500`} 
              inputProps={{ maxLength: 500 }} 
            />
          </Grid>

          {/* Date Closed */}
          {/* <Grid item xs={12} sm={6}>
            <TextField 
              margin="dense" 
              label="Date Closed" 
              type="date" 
              fullWidth 
              variant="outlined" 
              InputLabelProps={{ shrink: true }} 
              value={dateClosed} 
              onChange={(e) => setDateClosed(e.target.value)} 
            />
          </Grid> */}

          

          
          {/* Attachments Section */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachFileIcon sx={{ mr: 1 }} />
                Attachments
              </Typography>
            </Box>

            {/* Drag and Drop Area */}
            <Box
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                border: `2px dashed ${isDragging ? '#4e54c8' : '#e0e0e0'}`,
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                bgcolor: isDragging ? 'rgba(78, 84, 200, 0.04)' : 'transparent',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#4e54c8',
                  bgcolor: 'rgba(78, 84, 200, 0.04)'
                }
              }}
              onClick={() => document.getElementById('issue-file-upload').click()}
            >
              <input
                type="file"
                id="issue-file-upload"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <AttachFileIcon sx={{ fontSize: 40, color: '#4e54c8' }} />
                <Typography variant="body2" color="textSecondary">
                  Drag and drop files here or click to upload
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {uploadedFiles.length}/{MAX_FILE_COUNT} files
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                  Max file size can be {MAX_FILE_SIZE_MB} mb.
                </Typography>
              </Box>
            </Box>

            {/* File List */}
            <List>
              {uploadedFiles.map((file, idx) => (
                <ListItem key={file.id || idx}>
                  <ListItemText 
                    primary={file.name} 
                    secondary={file.uploadedAt ? new Date(file.uploadedAt).toLocaleString() : ''}
                  />
                  <IconButton 
                    edge="end" 
                    onClick={() => file.id ? handleDeleteFile(file.id) : setUploadedFiles(prev => prev.filter((_, i) => i !== idx))}
                    sx={{ color: '#d32f2f' }}
                  >
                    <AttachFileIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
            
            {/* Upload Progress */}
            {Object.keys(uploadProgress).length > 0 && (
              <Box sx={{ mt: 2 }}>
                {Object.entries(uploadProgress).map(([fileId, progress]) => (
                  <Box key={fileId} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Uploading... {Math.round(progress)}%
                    </Typography>
                    <LinearProgress variant="determinate" value={progress} />
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Comments Section */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Comments
                currentUserId={currentUserInfo?.uid}
                currentUserEmail={currentUserInfo?.email}
                existingComments={comments}
                onCommentAdd={(comment) => {
                  const newComment = {
                    id: Date.now().toString(),
                    text: typeof comment === 'object' ? comment.text : comment,
                    authorId: currentUserInfo?.uid,
                    authorEmail: currentUserInfo?.email,
                    timestamp: new Date().toISOString(),
                    isEdited: false
                  };
                  setComments(prevComments => [...prevComments, newComment]);
                }}
                onCommentEdit={(commentId, updatedComment) => {
                  setComments(prevComments => prevComments.map(comment => {
                    if (comment.id === commentId) {
                      return {
                        ...comment,
                        text: updatedComment.text,
                        timestamp: updatedComment.timestamp,
                        isEdited: true,
                        editedAt: updatedComment.timestamp,
                        editedBy: {
                          id: currentUserInfo?.uid,
                          email: currentUserInfo?.email
                        }
                      };
                    }
                    return comment;
                  }));
                }}
                onCommentDelete={(commentId, shouldDeleteCompletely) => {
                  if (shouldDeleteCompletely) {
                    setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
                  } else {
                    setComments(prevComments => prevComments.map(comment => 
                      comment.id === commentId 
                        ? { ...comment, isDeleted: true }
                        : comment
                    ));
                  }
                }}
                isAuthenticated={!!currentUserInfo}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        bgcolor: '#e6f7fa',
        px: 2,
        py: 1,
        borderRadius: 2,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontSize: '12px',
        m: 0
      }}>
        {initialData && (
          <Box sx={{
            fontFamily: 'Roboto, Arial, sans-serif',
            fontSize: '12px',
            color: '#333',
            p: 0,
            m: 0,
            minWidth: 220,
            textAlign: 'left',
            flex: '0 0 auto',
            mr: 4
          }}>
            <div>Created by: <span style={{ fontWeight: 500 }}>{initialData.cardCreatedByUserEmail || ''}</span></div>
            <div>Created on: <span style={{ fontWeight: 500 }}>{formatTimestamp(initialData.cardCreationDate || initialData.createdAt)}</span></div>
            <div>Last updated by: <span style={{ fontWeight: 500 }}>{initialData.cardUpdatedByUserEmail || ''}</span></div>
            <div>Last updated on: <span style={{ fontWeight: 500 }}>{formatTimestamp(initialData.cardUpdatedOn || initialData.editedAt || initialData.updatedAt)}</span></div>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 0, m: 0, flex: '1 1 auto', justifyContent: 'flex-end' }}>
          <Button onClick={handleCancel} sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontSize: '13px', color: '#1976d2', fontWeight: 500 }}>CANCEL</Button>
          <Button onClick={handleSave} color="primary" variant="contained" sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontSize: '13px', fontWeight: 500 }}>SAVE</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddIssueCardDialog;
